'use client';

import React, { useState } from 'react';
import {
  MessageSquare,
  Bot,
  Zap,
  BarChart3,
  ShoppingCart,
  Phone,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Instagram,
  Globe,
  Layers,
  Users,
  ShieldCheck,
  Smartphone,
  ExternalLink,
  Kanban,
  Sliders,
  Send,
  Plus,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export function OmnichannelFeatureShowcase() {
  const [activeTab, setActiveTab] = useState<'inbox' | 'ai' | 'automation' | 'kanban' | 'ecommerce'>('inbox');

  const tabs = [
    {
      id: 'inbox' as const,
      label: 'Omnichannel Inbox',
      icon: MessageSquare,
      badge: 'WhatsApp + IG + FB',
      title: 'One Unified Inbox for WhatsApp, Instagram & Messenger',
      subtitle:
        'Eliminate chaotic app-switching. Connect your WhatsApp numbers, Instagram Creator/Business accounts, and Facebook Pages into a single collaborative workspace.',
      highlights: [
        'Real-time WebSocket message streaming with sub-50ms sync',
        'Distinct channel badges (WhatsApp Green, Instagram Gradient, Messenger Blue)',
        'Floating Chat Dock to manage multiple customer conversations simultaneously',
        'Collision detection & live online presence so agents never overlap',
      ],
      previewType: 'inbox',
    },
    {
      id: 'ai' as const,
      label: 'Autonomous AI Agent',
      icon: Bot,
      badge: '24/7 Auto-Closing',
      title: 'Context-Aware AI Agents Grounded in Your Knowledge Base',
      subtitle:
        'Train your AI agent on product documentation, PDFs, and website FAQs. Zeric handles tier-1 customer inquiries, recommends products, and escalates to humans seamlessly.',
      highlights: [
        'Under 800ms average response time around the clock',
        'Zero-hallucination responses with strict knowledge grounding',
        'Supports 95+ languages with natural tone and dialect matching',
        'Smart tool usage: sends product cards, books appointments, assigns tags',
      ],
      previewType: 'ai',
    },
    {
      id: 'automation' as const,
      label: 'Flow Builder',
      icon: Zap,
      badge: 'Drag & Drop',
      title: 'Visual Workflow Automation with Zero Coding',
      subtitle:
        'Build multi-step automated journeys. Trigger flows based on incoming message keywords, funnel stage transitions, webhook payloads, or scheduled delays.',
      highlights: [
        'Drag-and-drop canvas powered by visual node architecture',
        'Conditional branching based on contact tags and lead scores',
        'Timed follow-up sequences (e.g. follow up in 2 hours if no reply)',
        'Automated webhook dispatchers to sync with external CRMs & ERPs',
      ],
      previewType: 'automation',
    },
    {
      id: 'kanban' as const,
      label: 'Pipeline & CRM',
      icon: Kanban,
      badge: 'Visual Deals',
      title: 'Visual Sales Pipeline & Contact Management',
      subtitle:
        'Track every customer from first contact to deal won. Drag-and-drop deals across stages, manage custom contact fields, view conversation history, and track team quotas.',
      highlights: [
        'Drag-and-drop Kanban board with custom stages (Lead, Qualified, Won)',
        'Rich contact profiles with custom fields, tags, and interaction logs',
        'Automated stage movement when payments are received or appointments booked',
        'Team revenue tracking and closing rate analytics',
      ],
      previewType: 'kanban',
    },
    {
      id: 'ecommerce' as const,
      label: 'E-Commerce & Voice',
      icon: ShoppingCart,
      badge: 'In-Chat Checkout',
      title: 'In-Chat Product Menus, Payments & WebRTC Voice Calls',
      subtitle:
        'Turn conversations into transactions. Send rich interactive catalogs, generate 1-click checkout links, and initiate browser-based voice calls without leaving the inbox.',
      highlights: [
        'Interactive product catalogs sent directly in WhatsApp and Instagram',
        'Integrated Stripe, Razorpay, and offline payment links',
        'Browser-based WebRTC voice calling with call recording and logs',
        'Automatic order confirmation receipts sent to the customer',
      ],
      previewType: 'ecommerce',
    },
  ];

  const currentTab = tabs.find((t) => t.id === activeTab)!;

  return (
    <div className="relative mx-auto max-w-6xl w-full text-left">
      {/* Section Header */}
      <div className="text-center mb-12 max-w-3xl mx-auto">
        <Badge
          variant="secondary"
          className="mb-3 px-3.5 py-1 rounded-full text-xs font-semibold border-primary/20 bg-primary/5 text-primary gap-1.5"
        >
          <Sparkles className="size-3.5" />
          Enterprise Feature Architecture
        </Badge>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
          Engineered for Omnichannel Scale
        </h2>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
          Everything your sales and support teams need to handle millions of customer conversations effortlessly across all Meta platforms.
        </p>
      </div>

      {/* Tabs Selector Bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 justify-start lg:justify-center no-scrollbar select-none">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-2xl text-xs sm:text-sm font-semibold transition-all shrink-0 flex items-center gap-2 border ${
                isActive
                  ? 'bg-foreground text-background border-foreground shadow-md scale-102'
                  : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted/60 border-border/80'
              }`}
            >
              <Icon className="size-4" />
              <span>{tab.label}</span>
              <Badge
                className={`text-[9px] px-1.5 py-0 h-4 border-none ${
                  isActive
                    ? 'bg-background/20 text-background'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {tab.badge}
              </Badge>
            </button>
          );
        })}
      </div>

      {/* Active Tab Showcase Box */}
      <div className="rounded-3xl border border-border/80 bg-card/90 p-6 md:p-10 shadow-2xl backdrop-blur-xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        
        {/* Left: Text & Features List */}
        <div className="lg:col-span-5 space-y-6">
          <div>
            <Badge className="bg-primary/10 text-primary border-primary/20 text-xs font-semibold mb-3">
              {currentTab.badge}
            </Badge>
            <h3 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight leading-snug">
              {currentTab.title}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed mt-3">
              {currentTab.subtitle}
            </p>
          </div>

          <div className="space-y-3 pt-2">
            {currentTab.highlights.map((item, idx) => (
              <div key={idx} className="flex items-start gap-2.5">
                <div className="size-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                  <CheckCircle2 className="size-3.5" />
                </div>
                <span className="text-xs sm:text-sm text-foreground font-medium">
                  {item}
                </span>
              </div>
            ))}
          </div>

          <div className="pt-4 flex items-center gap-3">
            <LinkToRegister label="Try This Feature Live" />
          </div>
        </div>

        {/* Right: Visual Interactive UI Mockup */}
        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-border/80 bg-background/95 p-5 shadow-lg relative overflow-hidden ring-1 ring-border/50 min-h-[380px] flex flex-col justify-center">
            
            {/* PREVIEW: OMNICHANNEL INBOX */}
            {currentTab.previewType === 'inbox' && (
              <div className="space-y-3 select-none text-left">
                <div className="flex items-center justify-between pb-3 border-b border-border/60">
                  <div className="flex items-center gap-2">
                    <span className="size-3 rounded-full bg-emerald-500" />
                    <span className="text-xs font-bold text-foreground">Unified Streams (3 Channels)</span>
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-600 text-[10px] font-mono border-none">
                    Pusher WebSocket Connected
                  </Badge>
                </div>

                {/* WhatsApp Message Item */}
                <div className="p-3 bg-muted/40 rounded-xl border border-border/60 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="size-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">
                      WA
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold">Emma Watson</span>
                        <Badge className="bg-emerald-600 text-white text-[9px] h-3.5 px-1 font-semibold border-none">
                          WhatsApp
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate max-w-[220px]">
                        Can you send the product catalog for wholesale?
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-none font-semibold">
                    AI Replied
                  </Badge>
                </div>

                {/* Instagram Message Item */}
                <div className="p-3 bg-card rounded-xl border border-pink-500/30 shadow-xs flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="size-9 rounded-full bg-gradient-to-tr from-purple-600 to-pink-500 text-white flex items-center justify-center font-bold text-xs">
                      <Instagram className="size-4" />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold">@fashion_luxe</span>
                        <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[9px] h-3.5 px-1 font-semibold border-none">
                          Instagram DM
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate max-w-[220px]">
                        Loving your new feature update! How do I connect my account?
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] bg-purple-500/10 text-purple-600 border-none font-semibold">
                    Assigned: Sarah
                  </Badge>
                </div>

                {/* Facebook Message Item */}
                <div className="p-3 bg-muted/40 rounded-xl border border-border/60 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="size-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xs">
                      FB
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold">David Chen</span>
                        <Badge className="bg-blue-600 text-white text-[9px] h-3.5 px-1 font-semibold border-none">
                          Messenger
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate max-w-[220px]">
                        Scheduled an appointment for Tuesday at 3 PM.
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-600 border-none font-semibold">
                    Flow Triggered
                  </Badge>
                </div>

                <div className="p-2.5 bg-primary/5 rounded-xl border border-primary/20 text-center text-xs font-semibold text-primary">
                  ✦ Floating Chat Dock active · Pop out any chat to multitask seamlessly
                </div>
              </div>
            )}

            {/* PREVIEW: AI AGENT */}
            {currentTab.previewType === 'ai' && (
              <div className="space-y-3 select-none text-left">
                <div className="flex items-center justify-between pb-3 border-b border-border/60">
                  <div className="flex items-center gap-2">
                    <Bot className="size-4 text-primary" />
                    <span className="text-xs font-bold text-foreground">Zeric Autonomous Reasoning Stream</span>
                  </div>
                  <span className="text-[11px] font-mono text-emerald-600 font-bold">280ms latency</span>
                </div>

                <div className="p-3.5 bg-muted/40 rounded-xl border border-border/60 space-y-2">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-bold text-muted-foreground uppercase tracking-wider">Vector Memory Grounding</span>
                    <span className="text-primary font-mono font-semibold">Match Score: 98.7%</span>
                  </div>
                  <p className="text-xs text-foreground font-mono bg-background p-2 rounded-lg border border-border/60">
                    Source: knowledge_base/pricing_and_meta_api_guide.pdf (chunk #14)
                  </p>
                </div>

                <div className="p-3.5 bg-primary/5 border border-primary/20 rounded-xl space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-primary">
                    <Sparkles className="size-3.5" />
                    Synthesized Omnichannel Response
                  </div>
                  <p className="text-xs text-foreground leading-relaxed">
                    &quot;Yes, Zeric supports both WhatsApp Official Cloud API and Instagram Direct Messaging simultaneously. Your customer will be able to check out with 1 click.&quot;
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 text-center text-xs">
                  <div className="bg-muted/40 p-2 rounded-xl border border-border/60">
                    <div className="font-bold font-mono text-foreground">OpenAI &amp; Groq</div>
                    <div className="text-[10px] text-muted-foreground">Neural Backbones</div>
                  </div>
                  <div className="bg-muted/40 p-2 rounded-xl border border-border/60">
                    <div className="font-bold font-mono text-emerald-600">Zero Hallucination</div>
                    <div className="text-[10px] text-muted-foreground">RAG Verification</div>
                  </div>
                </div>
              </div>
            )}

            {/* PREVIEW: AUTOMATION FLOW */}
            {currentTab.previewType === 'automation' && (
              <div className="space-y-3 select-none text-left">
                <div className="flex items-center justify-between pb-3 border-b border-border/60">
                  <div className="flex items-center gap-2">
                    <Zap className="size-4 text-amber-500" />
                    <span className="text-xs font-bold text-foreground">Visual Node Canvas Preview</span>
                  </div>
                  <Badge className="bg-amber-500/10 text-amber-600 text-[10px] font-semibold border-none">
                    Active Sequence
                  </Badge>
                </div>

                <div className="space-y-2">
                  <div className="p-2.5 bg-background border border-border rounded-xl flex items-center gap-3">
                    <div className="size-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">1</div>
                    <div className="flex-1 text-xs">
                      <strong className="block text-foreground">Keyword Trigger: &quot;demo&quot; or &quot;pricing&quot;</strong>
                      <span className="text-muted-foreground text-[11px]">Listens on WhatsApp &amp; Instagram</span>
                    </div>
                  </div>

                  <div className="p-2.5 bg-background border border-primary/30 rounded-xl flex items-center gap-3">
                    <div className="size-7 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs">2</div>
                    <div className="flex-1 text-xs">
                      <strong className="block text-foreground">Condition: Is Lead in Funnel?</strong>
                      <span className="text-muted-foreground text-[11px]">If No -&gt; Create Contact &amp; Tag #new_prospect</span>
                    </div>
                  </div>

                  <div className="p-2.5 bg-background border border-border rounded-xl flex items-center gap-3">
                    <div className="size-7 rounded-lg bg-purple-600 text-white flex items-center justify-center font-bold text-xs">3</div>
                    <div className="flex-1 text-xs">
                      <strong className="block text-foreground">Delay Action: Wait 15 Minutes</strong>
                      <span className="text-muted-foreground text-[11px]">If unread -&gt; Send follow-up voice invitation card</span>
                    </div>
                  </div>
                </div>

                <div className="p-2.5 bg-muted/40 rounded-xl text-center text-xs text-muted-foreground font-medium">
                  ✦ Fully customizable with drag &amp; drop node connector
                </div>
              </div>
            )}

            {/* PREVIEW: KANBAN & CRM */}
            {currentTab.previewType === 'kanban' && (
              <div className="space-y-3 select-none text-left">
                <div className="flex items-center justify-between pb-3 border-b border-border/60">
                  <div className="flex items-center gap-2">
                    <Kanban className="size-4 text-blue-500" />
                    <span className="text-xs font-bold text-foreground">Live Pipeline Funnel</span>
                  </div>
                  <span className="text-xs font-mono font-bold text-emerald-600">$48,200 Active Deals</span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs">
                  {/* Column 1 */}
                  <div className="bg-muted/30 p-2.5 rounded-xl border border-border/60 space-y-2">
                    <div className="flex items-center justify-between font-bold text-[11px] text-muted-foreground">
                      <span>New Leads</span>
                      <span>(12)</span>
                    </div>
                    <div className="p-2 bg-background rounded-lg border border-border shadow-xs space-y-1">
                      <div className="font-bold text-foreground text-[11px]">Nexus Digital</div>
                      <Badge className="bg-emerald-600 text-white text-[8px] h-3.5 px-1 border-none">WhatsApp</Badge>
                      <div className="text-[10px] text-muted-foreground">$2,400</div>
                    </div>
                  </div>

                  {/* Column 2 */}
                  <div className="bg-muted/30 p-2.5 rounded-xl border border-border/60 space-y-2">
                    <div className="flex items-center justify-between font-bold text-[11px] text-muted-foreground">
                      <span>Negotiation</span>
                      <span>(5)</span>
                    </div>
                    <div className="p-2 bg-background rounded-lg border border-primary/30 shadow-xs space-y-1">
                      <div className="font-bold text-foreground text-[11px]">Veloce Studio</div>
                      <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[8px] h-3.5 px-1 border-none">Instagram</Badge>
                      <div className="text-[10px] font-bold text-emerald-600">$8,500</div>
                    </div>
                  </div>

                  {/* Column 3 */}
                  <div className="bg-muted/30 p-2.5 rounded-xl border border-border/60 space-y-2">
                    <div className="flex items-center justify-between font-bold text-[11px] text-muted-foreground">
                      <span>Closed Won</span>
                      <span>(28)</span>
                    </div>
                    <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20 shadow-xs space-y-1">
                      <div className="font-bold text-foreground text-[11px]">Apex Logistics</div>
                      <Badge className="bg-blue-600 text-white text-[8px] h-3.5 px-1 border-none">Messenger</Badge>
                      <div className="text-[10px] font-bold text-emerald-600">$14,000 · Paid</div>
                    </div>
                  </div>
                </div>

                <div className="p-2.5 bg-blue-500/5 rounded-xl border border-blue-500/20 text-center text-xs font-semibold text-blue-600 dark:text-blue-400">
                  ✦ Drag &amp; drop cards to auto-trigger customer receipts and agent tasks
                </div>
              </div>
            )}

            {/* PREVIEW: ECOMMERCE & VOICE */}
            {currentTab.previewType === 'ecommerce' && (
              <div className="space-y-3 select-none text-left">
                <div className="flex items-center justify-between pb-3 border-b border-border/60">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="size-4 text-emerald-600" />
                    <span className="text-xs font-bold text-foreground">In-Chat Product Catalog &amp; WebRTC Voice</span>
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-600 text-[10px] font-semibold border-none">
                    Stripe &amp; Razorpay Ready
                  </Badge>
                </div>

                <div className="p-3 bg-muted/40 rounded-xl border border-border/60 flex items-center gap-3">
                  <img
                    src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200&q=80"
                    alt="Smart Watch"
                    className="size-16 rounded-xl object-cover border border-border"
                  />
                  <div className="flex-1 min-w-0">
                    <h5 className="text-xs font-bold text-foreground">Chronos Pro Series Watch</h5>
                    <p className="text-[11px] text-muted-foreground">Waterproof · 14-day battery · Space Gray</p>
                    <div className="text-xs font-bold text-emerald-600 mt-1">$299.00</div>
                  </div>
                  <Button size="sm" className="h-8 text-xs bg-primary text-primary-foreground">
                    Buy in Chat
                  </Button>
                </div>

                <div className="p-3 bg-card rounded-xl border border-border/80 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="size-9 rounded-full bg-emerald-600/15 text-emerald-600 flex items-center justify-center">
                      <Phone className="size-4" />
                    </div>
                    <div>
                      <h5 className="text-xs font-bold text-foreground">WebRTC In-Browser Calling</h5>
                      <p className="text-[11px] text-muted-foreground">Zero hardware needed · HD voice with auto-recording</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-emerald-600 font-mono">
                    Ready
                  </Badge>
                </div>

                <div className="p-2.5 bg-muted/40 rounded-xl text-center text-xs text-muted-foreground font-medium">
                  ✦ Customers can order, pay, and call directly inside the chat window
                </div>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
}

function LinkToRegister({ label }: { label: string }) {
  return (
    <a
      href="/sign-up"
      className="inline-flex items-center justify-center rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2.5 text-xs sm:text-sm font-semibold shadow-md shadow-primary/20 transition-all gap-1.5"
    >
      <span>{label}</span>
      <ArrowRight className="size-3.5" />
    </a>
  );
}
